import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { FloatingPanel } from '@/components/ui/floating-panel'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { useTimelineStore } from '../stores/timeline-store'
import { useTimelineItemOverlayStore } from '../stores/timeline-item-overlay-store'
import { useFillerRemovalDialogStore } from '../stores/filler-removal-dialog-store'
import { applyFillerRemovalToItems } from '../stores/actions/silence-removal-actions'
import { scoreFillerRangesWithClap } from '../utils/filler-audio-confidence'
import {
  analyzeFillerWordsForItems,
  applyFillerPreviewOverlays,
  asSilenceRangesByItemId,
  clearFillerRemovalOverlays,
  FILLER_REMOVAL_PRESETS,
  FILLER_REMOVAL_ANALYZING_OVERLAY_ID,
  type FillerAudioConfidenceLevel,
  type FillerRemovalSettings,
} from '../utils/filler-word-removal-preview'

const FILLER_REMOVAL_PANEL_DEFAULT_BOUNDS = {
  x: -1,
  y: -1,
  width: 440,
  height: 520,
}

const FILLER_REMOVAL_PANEL_STORAGE_KEY = 'fastcut.filler-removal-panel'

function toInputValue(value: number): string {
  return Number.isFinite(value) ? String(value) : ''
}

function normalizeEntry(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ')
}

function parseEntries(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[\n,]+/)
        .map(normalizeEntry)
        .filter(Boolean),
    ),
  ).toSorted((left, right) => left.localeCompare(right))
}

function formatEntries(entries: readonly string[]): string {
  return entries.join(', ')
}

function areEntriesEqual(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((entry, index) => entry === right[index])
}

function areSettingsEqual(a: FillerRemovalSettings, b: FillerRemovalSettings): boolean {
  return (
    a.paddingMs === b.paddingMs &&
    a.maxSegmentMs === b.maxSegmentMs &&
    areEntriesEqual(a.fillerWords, b.fillerWords) &&
    areEntriesEqual(a.fillerPhrases, b.fillerPhrases)
  )
}

function getMatchingPreset(settings: FillerRemovalSettings) {
  return FILLER_REMOVAL_PRESETS.find((preset) => areSettingsEqual(preset.settings, settings)) ?? null
}

function getConfidenceTone(level: FillerAudioConfidenceLevel): string {
  switch (level) {
    case 'high':
      return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700'
    case 'medium':
      return 'border-amber-500/40 bg-amber-500/10 text-amber-700'
    case 'low':
      return 'border-rose-500/40 bg-rose-500/10 text-rose-700'
    default:
      return 'border-border/60 bg-muted/30 text-muted-foreground'
  }
}

function getConfidenceLabel(level: FillerAudioConfidenceLevel): string {
  switch (level) {
    case 'high':
      return '高置信度'
    case 'medium':
      return '中置信度'
    case 'low':
      return '低置信度'
    default:
      return '未评分'
  }
}

function getRangeId(itemId: string, startMs: number, endMs: number, text: string, index: number): string {
  return `${itemId}:${startMs}:${endMs}:${text}:${index}`
}

function renderRangeContext(range: { text: string; contextBefore?: string; contextAfter?: string }) {
  if (!range.contextBefore && !range.contextAfter) {
    return null
  }

  return (
    <span className="block text-[10px] text-muted-foreground">
      {range.contextBefore ? `${range.contextBefore} ` : ''}
      <span className="rounded bg-amber-500/15 px-1 py-0.5 font-medium text-amber-700">{range.text}</span>
      {range.contextAfter ? ` ${range.contextAfter}` : ''}
    </span>
  )
}

function createSelectedRangeIds(
  rangesByItemId: NonNullable<ReturnType<typeof useFillerRemovalDialogStore.getState>['rangesByItemId']>,
): Set<string> {
  const selectedIds = new Set<string>()
  for (const [itemId, ranges] of Object.entries(rangesByItemId)) {
    ranges.forEach((range, index) => {
      selectedIds.add(getRangeId(itemId, range.startMs, range.endMs, range.text, index))
    })
  }
  return selectedIds
}

function createHighConfidenceSelectedRangeIds(
  rangesByItemId: NonNullable<ReturnType<typeof useFillerRemovalDialogStore.getState>['rangesByItemId']>,
): Set<string> {
  const selectedIds = new Set<string>()
  for (const [itemId, ranges] of Object.entries(rangesByItemId)) {
    ranges.forEach((range, index) => {
      if (range.audioConfidence?.level === 'high') {
        selectedIds.add(getRangeId(itemId, range.startMs, range.endMs, range.text, index))
      }
    })
  }
  return selectedIds
}

function filterRangesBySelectedIds(
  rangesByItemId: NonNullable<ReturnType<typeof useFillerRemovalDialogStore.getState>['rangesByItemId']>,
  selectedRangeIds: ReadonlySet<string>,
) {
  return Object.fromEntries(
    Object.entries(rangesByItemId)
      .map(([itemId, ranges]) => [
        itemId,
        ranges.filter((range, index) =>
          selectedRangeIds.has(getRangeId(itemId, range.startMs, range.endMs, range.text, index)),
        ),
      ])
      .filter(([, ranges]) => ranges.length > 0),
  )
}

function summarizeSelectedRanges(
  itemIds: readonly string[],
  rangesByItemId: NonNullable<ReturnType<typeof useFillerRemovalDialogStore.getState>['rangesByItemId']>,
) {
  const items = itemIds.map((itemId) => {
    const ranges = rangesByItemId[itemId] ?? []
    const durationFrames = ranges.reduce((sum, range) => sum + range.durationFrames, 0)
    const durationSec = durationFrames / Math.max(1, useTimelineStore.getState().fps)
    return {
      itemId,
      rangeCount: ranges.length,
      durationFrames,
      durationSec,
    }
  })

  return {
    items,
    itemCount: items.length,
    itemsWithFillers: items.filter((item) => item.rangeCount > 0).length,
    totalRanges: items.reduce((sum, item) => sum + item.rangeCount, 0),
    totalDurationFrames: items.reduce((sum, item) => sum + item.durationFrames, 0),
    totalDurationSec: items.reduce((sum, item) => sum + item.durationSec, 0),
  }
}

export const FillerRemovalDialog = memo(function FillerRemovalDialog() {
  const isOpen = useFillerRemovalDialogStore((state) => state.isOpen)
  const itemIds = useFillerRemovalDialogStore((state) => state.itemIds)
  const settings = useFillerRemovalDialogStore((state) => state.settings)
  const rangesByItemId = useFillerRemovalDialogStore((state) => state.rangesByItemId)
  const summary = useFillerRemovalDialogStore((state) => state.summary)
  const isAnalyzing = useFillerRemovalDialogStore((state) => state.isAnalyzing)
  const error = useFillerRemovalDialogStore((state) => state.error)
  const close = useFillerRemovalDialogStore((state) => state.close)
  const setSettings = useFillerRemovalDialogStore((state) => state.setSettings)
  const setAnalyzing = useFillerRemovalDialogStore((state) => state.setAnalyzing)
  const setAnalysisResult = useFillerRemovalDialogStore((state) => state.setAnalysisResult)
  const setError = useFillerRemovalDialogStore((state) => state.setError)
  const resetAnalysis = useFillerRemovalDialogStore((state) => state.resetAnalysis)
  const upsertOverlay = useTimelineItemOverlayStore((state) => state.upsertOverlay)
  const [refreshToken, setRefreshToken] = useState(0)
  const [isApplying, setIsApplying] = useState(false)
  const [isScoringAudio, setIsScoringAudio] = useState(false)
  const [selectedRangeIds, setSelectedRangeIds] = useState<Set<string>>(new Set())
  const [wordDraft, setWordDraft] = useState('')
  const [phraseDraft, setPhraseDraft] = useState('')
  const previousItemIdsRef = useRef<string[]>([])
  const analyzedSettingsRef = useRef<FillerRemovalSettings | null>(null)
  const pendingSelectedRangeIdsRef = useRef<Set<string> | null>(null)
  const items = useTimelineStore((state) => state.items)

  const selectedItems = useMemo(() => {
    const itemById = new Map(items.map((item) => [item.id, item]))
    return itemIds
      .map((itemId) => itemById.get(itemId))
      .filter((item): item is NonNullable<typeof item> => item !== undefined)
  }, [itemIds, items])

  const itemIdsWithAnyCaptions = useMemo(() => {
    const ids = new Set<string>()
    for (const item of items) {
      if (item.type === 'text' && item.captionSource?.clipId) {
        ids.add(item.captionSource.clipId)
      }
    }
    return ids
  }, [items])

  const itemsWithCaptions = useMemo(
    () => selectedItems.filter((item) => itemIdsWithAnyCaptions.has(item.id)),
    [itemIdsWithAnyCaptions, selectedItems],
  )
  const applicableItemIds = useMemo(
    () => selectedItems.filter((item) => !itemIdsWithAnyCaptions.has(item.id)).map((item) => item.id),
    [itemIdsWithAnyCaptions, selectedItems],
  )
  const selectedRangesByItemId = useMemo(
    () => filterRangesBySelectedIds(rangesByItemId, selectedRangeIds),
    [rangesByItemId, selectedRangeIds],
  )
  const selectedSummary = useMemo(
    () => summarizeSelectedRanges(itemIds, selectedRangesByItemId),
    [itemIds, selectedRangesByItemId],
  )
  const canApplyRemoval =
    applicableItemIds.some((itemId) => (selectedRangesByItemId[itemId] ?? []).length > 0) &&
    applicableItemIds.length > 0
  const activePreset = useMemo(() => getMatchingPreset(settings), [settings])
  const totalDetectedRanges = summary?.totalRanges ?? 0

  useEffect(() => {
    setWordDraft(formatEntries(settings.fillerWords))
    setPhraseDraft(formatEntries(settings.fillerPhrases))
  }, [settings.fillerPhrases, settings.fillerWords])

  useEffect(() => {
    const nextSelectedIds = createSelectedRangeIds(rangesByItemId)
    if (pendingSelectedRangeIdsRef.current) {
      const pending = pendingSelectedRangeIdsRef.current
      pendingSelectedRangeIdsRef.current = null
      setSelectedRangeIds(new Set([...pending].filter((id) => nextSelectedIds.has(id))))
      return
    }
    setSelectedRangeIds(nextSelectedIds)
  }, [rangesByItemId])

  useEffect(() => {
    if (!isOpen || isAnalyzing) {
      return
    }

    applyFillerPreviewOverlays(itemIds, selectedRangesByItemId)
  }, [isAnalyzing, isOpen, itemIds, selectedRangesByItemId])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const previousItemIds = previousItemIdsRef.current
    if (previousItemIds.length > 0) {
      clearFillerRemovalOverlays(previousItemIds)
    }
    previousItemIdsRef.current = itemIds

    if (itemIds.length === 0) {
      analyzedSettingsRef.current = null
      resetAnalysis()
      return
    }

    let cancelled = false

    const run = async () => {
      setAnalyzing(true)
      setError(null)
      clearFillerRemovalOverlays(itemIds)
      for (const itemId of itemIds) {
        upsertOverlay(itemId, {
          id: FILLER_REMOVAL_ANALYZING_OVERLAY_ID,
          label: '检测口头禅中',
          tone: 'info',
        })
      }

      try {
        const nextRangesByItemId = await analyzeFillerWordsForItems(itemIds, settings)
        if (cancelled) {
          return
        }
        const nextSummary = applyFillerPreviewOverlays(itemIds, nextRangesByItemId)
        analyzedSettingsRef.current = { ...settings }
        setAnalysisResult(nextRangesByItemId, nextSummary)
      } catch (analysisError) {
        if (cancelled) {
          return
        }
        analyzedSettingsRef.current = null
        clearFillerRemovalOverlays(itemIds)
        setError(analysisError instanceof Error ? analysisError.message : '口头禅检测失败，请稍后重试。')
      } finally {
        if (!cancelled) {
          setAnalyzing(false)
        }
      }
    }

    void run()

    return () => {
      cancelled = true
      clearFillerRemovalOverlays(itemIds)
    }
  }, [
    isOpen,
    itemIds,
    refreshToken,
    resetAnalysis,
    setAnalyzing,
    setAnalysisResult,
    setError,
    settings,
    upsertOverlay,
  ])

  if (!isOpen) {
    return null
  }

  const handleSettingChange = (key: 'paddingMs' | 'maxSegmentMs', rawValue: string) => {
    const parsed = Number(rawValue)
    if (!Number.isFinite(parsed)) {
      return
    }
    setSettings({ [key]: parsed })
  }

  const handleClose = () => {
    clearFillerRemovalOverlays(itemIds)
    close()
  }

  const handleApply = async () => {
    if (!canApplyRemoval || isApplying) {
      return
    }

    const analyzedSettings = analyzedSettingsRef.current
    if (!analyzedSettings || !areSettingsEqual(analyzedSettings, settings)) {
      toast.info('参数已变更，请先重新检测后再应用。')
      return
    }

    setIsApplying(true)
    try {
      const applicableRangesByItemId = Object.fromEntries(
        applicableItemIds.map((itemId) => [itemId, selectedRangesByItemId[itemId] ?? []]),
      )
      const result = applyFillerRemovalToItems(
        applicableItemIds,
        asSilenceRangesByItemId(applicableRangesByItemId),
      )
      clearFillerRemovalOverlays(itemIds)
      if (result.processedItemCount === 0) {
        toast.info('当前预览没有可应用的口头禅片段')
      } else {
        const extraSkippedCount = itemsWithCaptions.length + result.skippedItemCount
        toast.success(
          extraSkippedCount > 0
            ? `已移除 ${result.removedSegmentCount} 段口头禅，收紧 ${result.shiftedItemCount} 个片段，跳过 ${extraSkippedCount} 个片段`
            : `已移除 ${result.removedSegmentCount} 段口头禅，收紧 ${result.shiftedItemCount} 个片段`,
        )
      }
      close()
    } catch (applyError) {
      toast.error(applyError instanceof Error ? applyError.message : '应用口头禅移除失败')
    } finally {
      setIsApplying(false)
    }
  }

  const handleApplyPreset = (nextSettings: FillerRemovalSettings) => {
    setSettings(nextSettings)
    setWordDraft(formatEntries(nextSettings.fillerWords))
    setPhraseDraft(formatEntries(nextSettings.fillerPhrases))
  }

  const handleAudioReview = async () => {
    if (isAnalyzing || isScoringAudio || totalDetectedRanges === 0) {
      return
    }

    setIsScoringAudio(true)
    try {
      const scoredRangesByItemId = await scoreFillerRangesWithClap(rangesByItemId)

      const highConfidenceIds = createHighConfidenceSelectedRangeIds(scoredRangesByItemId)
      pendingSelectedRangeIdsRef.current = highConfidenceIds.size > 0 ? highConfidenceIds : selectedRangeIds
      setAnalysisResult(scoredRangesByItemId, summary ?? summarizeSelectedRanges(itemIds, scoredRangesByItemId))
      if (highConfidenceIds.size > 0) {
        toast.success(`音频复核完成，已优先选中 ${highConfidenceIds.size} 个高置信度命中项。`)
      } else {
        toast.info('音频复核完成，未发现高置信度命中，已保留当前选择。')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '音频复核失败')
    } finally {
      setIsScoringAudio(false)
    }
  }

  return (
    <FloatingPanel
      title="口头禅移除预览"
      defaultBounds={FILLER_REMOVAL_PANEL_DEFAULT_BOUNDS}
      minWidth={360}
      minHeight={420}
      storageKey={FILLER_REMOVAL_PANEL_STORAGE_KEY}
      onClose={handleClose}
    >
      <section className="flex h-full min-h-0 flex-col overflow-hidden bg-background px-4 py-3 text-sm">
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="space-y-4">
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">已选片段</div>
              <div className="text-xs text-muted-foreground">
                {selectedItems.length > 0
                  ? selectedItems.map((item) => item.label).join('、')
                  : '当前没有可检测口头禅的片段。'}
              </div>
              <div className="text-xs text-muted-foreground">
                当前优先使用 word-level 时间戳做检测；没有逐词结果时会自动回退到 segment 级。
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-foreground">检测预设</div>
                <div className="text-[11px] text-muted-foreground">
                  {activePreset ? `当前：${activePreset.label}` : '当前：自定义'}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {FILLER_REMOVAL_PRESETS.map((preset) => {
                  const isActive = activePreset?.key === preset.key
                  return (
                    <button
                      key={preset.key}
                      type="button"
                      className={`rounded-md border px-3 py-2 text-left text-xs transition ${
                        isActive
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-border/70 bg-background text-foreground hover:border-border'
                      }`}
                      onClick={() => handleApplyPreset(preset.settings)}
                    >
                      <div className="font-medium">{preset.label}</div>
                      <div className={isActive ? 'text-background/80' : 'text-muted-foreground'}>
                        {preset.description}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="filler-max-segment-ms">最大片段 ms</Label>
                <Input
                  id="filler-max-segment-ms"
                  type="number"
                  step="100"
                  value={toInputValue(settings.maxSegmentMs)}
                  onChange={(event) => handleSettingChange('maxSegmentMs', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="filler-padding-ms">前后留白 ms</Label>
                <Input
                  id="filler-padding-ms"
                  type="number"
                  step="20"
                  value={toInputValue(settings.paddingMs)}
                  onChange={(event) => handleSettingChange('paddingMs', event.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="filler-words">口头禅词</Label>
                <textarea
                  id="filler-words"
                  className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
                  value={wordDraft}
                  onChange={(event) => setWordDraft(event.target.value)}
                  onBlur={() => setSettings({ fillerWords: parseEntries(wordDraft) })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="filler-phrases">口头禅短语</Label>
                <textarea
                  id="filler-phrases"
                  className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
                  value={phraseDraft}
                  onChange={(event) => setPhraseDraft(event.target.value)}
                  onBlur={() => setSettings({ fillerPhrases: parseEntries(phraseDraft) })}
                />
              </div>
            </div>

            <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                预览结果
              </div>
              <div className="mt-2 space-y-1 text-sm">
                <div>片段数：{selectedSummary.itemCount || selectedItems.length}</div>
                <div>已选命中的片段：{selectedSummary.itemsWithFillers}</div>
                <div>已选命中段数：{selectedSummary.totalRanges}</div>
                <div>已选口头禅总时长：{selectedSummary.totalDurationSec.toFixed(1)}s</div>
                <div className="text-xs text-muted-foreground">
                  {`总检测段数 ${totalDetectedRanges}，可取消不想裁切的命中项。`}
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                当前复用现有片段切分与收紧逻辑，不会做整轨 ripple delete。
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                可选音频复核会进一步按声音特征筛掉疑似误判，并优先保留高置信度命中。
              </div>
              {itemsWithCaptions.length > 0 && (
                <div className="mt-3 text-xs text-amber-600">
                  当前选中片段中有自动字幕。为了避免把现有字幕替换流带坏，这一版会自动跳过这些片段，其余片段继续应用。
                </div>
              )}
              {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
            </div>

            <div className="rounded-lg border border-border/70 bg-background/70 p-3">
              <div className="space-y-2">
            {totalDetectedRanges > 0 && (
              <div className="flex items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/20 px-2 py-1.5 text-[11px] text-muted-foreground">
                <span>{`已选择 ${selectedSummary.totalRanges}/${totalDetectedRanges} 个命中项`}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="text-foreground underline-offset-2 hover:underline"
                    onClick={() => setSelectedRangeIds(createSelectedRangeIds(rangesByItemId))}
                  >
                    全选
                  </button>
                  <button
                    type="button"
                    className="text-foreground underline-offset-2 hover:underline"
                    onClick={() => setSelectedRangeIds(new Set())}
                  >
                    清空
                  </button>
                </div>
              </div>
            )}
            {selectedSummary.items.map((itemSummary) => {
              const item = selectedItems.find((candidate) => candidate.id === itemSummary.itemId)
              const itemRanges = rangesByItemId[itemSummary.itemId] ?? []
              return (
                <div key={itemSummary.itemId} className="rounded-md border border-border/60 p-2">
                  <div className="font-medium text-foreground">{item?.label ?? itemSummary.itemId}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {itemSummary.rangeCount > 0
                      ? `口头禅 ${itemSummary.rangeCount} 段，约 ${itemSummary.durationSec.toFixed(1)}s`
                      : '未检测到口头禅'}
                  </div>
                  {itemRanges.length > 0 && (
                    <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                      {itemRanges.slice(0, 4).map((range, index) => (
                        <label
                          key={`${itemSummary.itemId}-${index}`}
                          className="flex cursor-pointer items-start gap-2 rounded border border-border/40 px-2 py-1"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={selectedRangeIds.has(
                              getRangeId(itemSummary.itemId, range.startMs, range.endMs, range.text, index),
                            )}
                            onChange={(event) => {
                              const rangeId = getRangeId(
                                itemSummary.itemId,
                                range.startMs,
                                range.endMs,
                                range.text,
                                index,
                              )
                              setSelectedRangeIds((current) => {
                                const next = new Set(current)
                                if (event.target.checked) {
                                  next.add(rangeId)
                                } else {
                                  next.delete(rangeId)
                                }
                                return next
                              })
                            }}
                          />
                          <span className="space-y-1">
                            <span className="block text-foreground">
                              {`#${index + 1} ${range.startMs}ms - ${range.endMs}ms`}
                            </span>
                            <span className="flex flex-wrap items-center gap-1">
                              <span className="inline-flex rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                                {range.text}
                              </span>
                              <span
                                className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium ${getConfidenceTone(range.audioConfidence?.level ?? 'unknown')}`}
                              >
                                {getConfidenceLabel(range.audioConfidence?.level ?? 'unknown')}
                              </span>
                            </span>
                            {renderRangeContext(range)}
                          </span>
                        </label>
                      ))}
                      {itemRanges.length > 4 && <div>{`还有 ${itemRanges.length - 4} 段未展开`}</div>}
                    </div>
                  )}
                </div>
              )
            })}
            {!summary && !isAnalyzing && !error && (
              <div className="text-xs text-muted-foreground">调整参数后会自动重新检测。</div>
            )}
            {isAnalyzing && <div className="text-xs text-muted-foreground">正在根据转录文本检测口头禅…</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex shrink-0 items-center justify-between gap-2 border-t border-border/60 pt-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setRefreshToken((token) => token + 1)}>
              重新检测
            </Button>
            <Button variant="outline" onClick={handleAudioReview} disabled={isAnalyzing || isScoringAudio || totalDetectedRanges === 0}>
              {isScoringAudio ? '音频复核中...' : '音频复核'}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={handleClose}>
              关闭
            </Button>
            <Button onClick={handleApply} disabled={!canApplyRemoval || isAnalyzing || isApplying}>
              {isApplying ? '应用中...' : applicableItemIds.length < selectedItems.length ? '应用可用片段' : '应用预览'}
            </Button>
          </div>
        </div>
      </section>
    </FloatingPanel>
  )
})